import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import MaskedInput from 'react-text-mask';
import { ArrowUpRight } from 'react-feather';

import { FormikComboField, FormikTextAreaField, FormikTextField } from './utils/fields';

import { MASK_PHONE_US } from 'common/modules/mask-text/masks';
import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { emailValidator, phoneNumberValidator } from 'common/modules/form-validation/validators';
import classNames from 'classnames';
import { DatePicker } from 'components/date-picker';
import {
    emptyGiftDetailsRequest,
    GiftDetailsRequest,
    ProductGiftDetailsContext,
} from './ProductGiftDetails';
import { getGiftCardImgSrc } from './EGiftTabPanel';
import { useHistory } from 'react-router-dom';
import { Root, Trigger, Content } from 'components/popover/Popover';
import { colors } from 'styles/settings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import useMediaQueries from 'hooks/useMediaQueries';
import { Modal } from 'components/modal/Modal';
import { useModalControls } from 'hooks/useModalControls';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectCartHasGiftingItems } from 'common/features/store/duck/cart/duck';
import { Routes } from 'routes';
import useFeatureFlags from 'common/hooks/useFeatureFlags';

const styles = createStyles({
    btns: ['w-80 md:w-max'],
    sendersInfo: ['grid grid-cols-2 gap-4'],
    formContent: ['grid grid-cols-2 gap-x-4 max-w-xl text-left'],
    title: 'col-span-1 md:col-span-2 text-lg text-left text-brand-dark font-bold mt-6',
    fieldValues: 'col-span-2 grid grid-cols-1 md:grid-cols-2 gap-y-2 md:gap-x-2',
    pillBtn: [
        'rounded-full text-brand inline-block truncate align-middle cursor-pointer text-base font-semibold border border-brand',
        { padding: '14px 21px' },
    ],
});

export const GiftDetailsEditForm = () => {
    const { formData, setFormData, setHasFormData, setIsEditing } =
        useContext(ProductGiftDetailsContext);

    const onSubmit = (data: GiftDetailsRequest) => {
        setFormData(data);
        setHasFormData(true);
        setIsEditing(false);
    };

    const validateFormData = (formValues: GiftDetailsRequest) => {
        const errors: { [key: string]: string } = {};

        if (!formValues.RecipientEmail) {
            errors.RecipientEmail = 'required';
        }
        if (!formValues.SenderName) {
            errors.SenderName = 'required';
        }
        if (!formValues.SenderPhone) {
            errors.SenderPhone = 'required';
        }
        if (!formValues.SelectedCardCoverImage || Number(formValues.SelectedCardCoverImage) <= 0) {
            errors.SelectedCardCoverImage = 'required';
        }

        return errors;
    };

    return (
        <Formik initialValues={formData} onSubmit={onSubmit} validate={validateFormData}>
            <EditForm />
        </Formik>
    );
};

const tomorrow = () => {
    const d = new Date();
    d.setHours(0);
    d.setMinutes(0);
    d.setSeconds(0);
    d.setDate(d.getDate() + 1);
    return d;
};

const addMonthsFromNow = (months: number) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date;
};

const DateSelector = () => {
    const ctx = useFormikContext<GiftDetailsRequest>();

    const selectedDate = ctx.values.SelectedDate;
    const setSelectedDate = useCallback(
        (date: Date) => {
            ctx.setFieldValue('SelectedDate', date);
        },
        [ctx]
    );

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    return (
        <div className="flex items-center">
            <div>
                <button
                    type="button"
                    className={classNames(
                        styles.pillBtn,
                        selectedDate && selectedDate.toDateString() === new Date().toDateString()
                            ? 'bg-pale-blue o:border-2'
                            : 'bg-white'
                    )}
                    onClick={() => setSelectedDate(new Date())}
                >
                    Now
                </button>
            </div>
            <div className="flex items-center">
                <DatePicker
                    open={isDatePickerOpen}
                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                    onCalendarClose={() => setIsDatePickerOpen(false)}
                    selected={selectedDate}
                    onChange={(dt) => setSelectedDate(dt as Date)}
                    minDate={tomorrow()}
                    maxDate={addMonthsFromNow(6)}
                    customInput={
                        <button
                            type="button"
                            className={classNames(
                                'ml-3 mr-6',
                                styles.pillBtn,
                                selectedDate &&
                                    selectedDate.toDateString() !== new Date().toDateString()
                                    ? 'bg-pale-blue o:border-2'
                                    : 'bg-white'
                            )}
                        >
                            Select a Date
                        </button>
                    }
                />
                <span>{selectedDate?.toLocaleDateString()}</span>
            </div>
        </div>
    );
};

const EditForm = () => {
    const {
        formData,
        setFormData,
        setHasFormData,
        setIsEditing,
        setSendAsGift,
        hasFormData,
        giftCardImages,
        showEditForm,
    } = useContext(ProductGiftDetailsContext);
    const { values, errors, setFieldValue, submitForm } = useFormikContext<GiftDetailsRequest>();

    const onClose = () => {
        setIsEditing(false);
        if (!hasFormData) {
            setSendAsGift(false);
        }
    };

    const { SelectedCardCoverImage } = formData;

    useEffect(() => {
        setFieldValue('SelectedCardCoverImage', SelectedCardCoverImage);
    }, [SelectedCardCoverImage, setFieldValue]);

    const cardCoverImages = useMemo(() => {
        return giftCardImages.map((img, index) => ({
            value: img.ImageName,
            text: `Option ${index + 1}`,
        }));
    }, [giftCardImages]);

    const submit = () => submitForm();

    return (
        <Form name="EditForm">
            <div>
                <div className="grid grid-cols-2">
                    <div className="text-brand-dark text-2xl text-left">Gift Details</div>
                    <div className="block md:hidden ml-auto mr-2 flex items-center">
                        <button
                            onClick={() => {
                                setFormData(emptyGiftDetailsRequest);
                                setHasFormData(false);
                                setSendAsGift(false);
                            }}
                            className="text-brand text-sm block no-underline outline-none focus:outline-none"
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                            <span className="ml-2">Cancel</span>
                        </button>
                    </div>
                </div>
                <div>
                    <GiftType />
                    <div className={styles.fieldValues}>
                        <div className={styles.title}>Select Delivery Date</div>
                        <div>
                            <DateSelector />
                        </div>
                    </div>
                    <div>
                        <div className={styles.formContent}>
                            {values.SelectedDate && (
                                <>
                                    <div className={styles.fieldValues}>
                                        <div className={styles.title}>To</div>
                                        <div>
                                            <FormikTextField<GiftDetailsRequest>
                                                name="RecipientName"
                                                title={'Recipient’s Name'}
                                                label={'Recipient’s Name'}
                                                placeholder="Enter recipient’s full name"
                                                error={errors.RecipientName}
                                                maxLength={200}
                                            />
                                        </div>
                                        <div>
                                            <FormikTextField<GiftDetailsRequest>
                                                name="RecipientEmail"
                                                title={"Recipient's Email"}
                                                label={"Recipient's Email"}
                                                placeholder="example@email.com"
                                                error={errors.RecipientEmail}
                                                type="email"
                                                validate={emailValidator}
                                                maxLength={200}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.fieldValues}>
                                        <div className={styles.title}>From</div>
                                        <FormikTextField<GiftDetailsRequest>
                                            name="SenderName"
                                            title={"Sender's Name"}
                                            label={"Sender's Name"}
                                            placeholder="Enter sender’s full name"
                                            error={errors.SenderName}
                                            maxLength={200}
                                        />

                                        <MaskedInput
                                            mask={MASK_PHONE_US}
                                            guide={false}
                                            render={(ref, _props) => (
                                                <FormikTextField
                                                    {..._props}
                                                    title={"Sender's Phone Number"}
                                                    label={"Sender's Phone Number"}
                                                    inputRef={ref}
                                                    type="tel"
                                                    name="SenderPhone"
                                                    placeholder={'(000) 000-0000'}
                                                    validate={phoneNumberValidator}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className={styles.title}>Optional Message</div>
                                    <div className="col-span-2">
                                        <FormikTextAreaField
                                            name="GiftMessage"
                                            title={'Personal Message'}
                                            label={'Personal Message'}
                                            placeholder="Add a personal note"
                                            error={errors.GiftMessage}
                                            maxLength={150}
                                        />
                                    </div>

                                    <div className={styles.fieldValues}>
                                        <div className={styles.title}>Gift Card Image</div>
                                        <div>
                                            <FormikComboField
                                                name="SelectedCardCoverImage"
                                                label="Card Face"
                                                required={true}
                                                options={cardCoverImages}
                                                error={errors.SelectedCardCoverImage}
                                            />
                                        </div>
                                        <div>
                                            <img
                                                src={getGiftCardImgSrc(
                                                    giftCardImages,
                                                    values.SelectedCardCoverImage || ''
                                                )}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="flex text-center w-full col-span-2">
                                <div className="flex flex-col-reverse md:flex-row md:inline-block ml-auto">
                                    <div className="inline-block mt-2 md:mt-0 mr-2">
                                        <Btn
                                            type="button"
                                            isSecondary={true}
                                            className={styles.btns}
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Btn>
                                    </div>
                                    <div className="inline-block w-80 md:w-max mt-8">
                                        <Btn
                                            className={styles.btns}
                                            disabled={showEditForm && !values.SelectedDate}
                                            onClick={submit}
                                        >
                                            Save
                                        </Btn>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    );
};

let ModalContent: React.ElementType = React.Fragment;

const GiftType = () => {
    const history = useHistory();
    const [giftingType, setGiftingType] = useState<'Single' | 'Bulk'>('Single');
    const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);

    const flags = useFeatureFlags();
    const enableBulkGifting = flags.isEnabled('enable_bulk_egifting') || false;

    const isMobileView = useDetectMobileView();
    const modal = useModalControls();
    const cartHasGiftingItems = useRootSelector(selectCartHasGiftingItems);

    const chooseSingleGift = () => setGiftingType('Single');
    const goToBulkGiftingScreen = () => {
        if (isMobileView) {
            ModalContent = BulkOrderDisabledOnMobileWarning;
            modal.toggleModalVisibility();
        } else {
            if (cartHasGiftingItems) {
                ModalContent = BulkOrderDisabledGiftItemsWarning;
                modal.toggleModalVisibility();
            } else {
                history.push(history.location.pathname + '/gifting');
            }
        }
    };

    const onPopoverOpenChange = () => {
        setIsTooltipOpen((s: boolean) => !s);
    };

    return (
        <div>
            <div className={classNames(styles.title, 'flex items-center')}>
                Gift Type
                <Root open={isTooltipOpen} onOpenChange={onPopoverOpenChange}>
                    <Trigger className="h-4 ml-1 hidden sm:flex">
                        <FontAwesomeIcon
                            color={isTooltipOpen ? colors.brandDark : colors.brand}
                            className="sm:text-sm text-md -mt-1"
                            icon={faInfoCircle}
                        />
                    </Trigger>
                    <Content align="start" alignOffset={-20} side="top" sideOffset={10}>
                        <div className="font-semibold pb-1 text-grey-1 text-xs">Single Gift</div>
                        <p className="text-grey-1 text-xs pb-3">
                            This option is recommended if you are sending a single gift to one
                            recipient.
                        </p>
                        <span className="text-orange font-semibold text-xs">New!</span>
                        <div className="font-semibold pb-1 text-grey-1 text-xs">Bulk Order</div>
                        <p className="text-grey-1 text-xs">
                            This option is recommended if you are sending 5+ gifts to multiple
                            recipients (125 maximum). From there, you can download a template and
                            enter your recipient details.
                        </p>
                    </Content>
                </Root>
            </div>
            <div className="mt-3 flex flex-start items-center gap-6">
                <button
                    onClick={chooseSingleGift}
                    className={classNames(
                        styles.pillBtn,
                        giftingType === 'Single' ? 'bg-pale-blue o:border-2' : 'bg-white'
                    )}
                >
                    Single Gift
                </button>
                {enableBulkGifting && (
                    <button
                        type="button"
                        className="border-b border-brand text-brand font-semibold flex items-center outline-none focus:outline-none"
                        onClick={goToBulkGiftingScreen}
                    >
                        Bulk Order
                        <ArrowUpRight strokeWidth={3} className="h-4" />
                    </button>
                )}
            </div>
            <Modal
                title=""
                isOpen={modal.isVisible}
                onClose={modal.toggleModalVisibility}
                showCloseButton
            >
                <div className="text-center m-3">
                    <FontAwesomeIcon
                        size="2x"
                        color={colors.warningDark}
                        icon={faExclamationTriangle}
                    />
                    {ModalContent ? <ModalContent onClose={modal.toggleModalVisibility} /> : null}
                </div>
            </Modal>
        </div>
    );
};

const BulkOrderDisabledOnMobileWarning = ({ onClose }: { onClose: () => void }) => (
    <>
        <h1 className="block text-brand-dark font-semibold text-22px mt-3 mb-5">
            Bulk Order Information
        </h1>
        <p className="block text-grey-1 mb-10">
            This option is available on desktop versions of RaiseRight only. It is recommended if
            you are sending 5+ gifts to multiple recipients. From there, you can download a template
            and enter your recipient details.
        </p>
        <Btn onClick={onClose}>Okay</Btn>
    </>
);

// NOTE: This content will render in the modal when user has at least 1 line item in their cart
// that is a gift.
const BulkOrderDisabledGiftItemsWarning = ({ onClose }: { onClose: () => void }) => {
    const history = useHistory();
    const goToCart = () => history.push(Routes.ShopOrderSummary);

    return (
        <>
            <h1 className="block text-brand-dark font-semibold text-22px my-5">
                Gift Items in Cart
            </h1>
            <p className="block text-grey-1 mb-10">
                To continue, first checkout or remove gift items currently in your cart, then return
                to place a Bulk Order.
            </p>
            <div>
                <Btn className="mx-auto block mb-10" onClick={goToCart}>
                    Go to Cart
                </Btn>
                <button
                    type="button"
                    onClick={onClose}
                    className="mx-auto block underline text-brand text-lg font-semibold"
                >
                    Close
                </button>
            </div>
        </>
    );
};

const useDetectMobileView = (value = false) => {
    const [isMobile, setIsMobile] = useState<boolean>(value);
    useMediaQueries({
        '(max-width: 640px)': () => {
            setIsMobile(true);
        },
        '(min-width: 640px)': () => {
            setIsMobile(false);
        },
    });

    return isMobile;
};
